import Button from 'components/base/Button'
import FormItem from 'components/base/FormItem'
import { FC } from 'lib/component-utils'
import { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Updater } from 'use-immer'
import { RecoveryProcessState } from './lib/types'
import { useIntl } from 'lib/intl-utils'
import Input from 'components/base/Input'
import { useRouter } from 'next/router'
import { DuplicateIcon } from '@heroicons/react/outline'
import Tooltip from 'components/base/Tooltip'
import { useToast } from 'components/app/NotificationArea'
import Alert from 'components/base/Alert'

type Props = {
  processState: RecoveryProcessState
  setProcessState: Updater<RecoveryProcessState>
}

const ResetPasswordSuccess: FC<Props> = ({ processState, setProcessState }) => {
  const { t, lang } = useIntl()
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm<RecoveryProcessState>({ mode: 'onSubmit', defaultValues: processState })
  const router = useRouter()
  const showToast = useToast()

  const onUserDataSubmit = useCallback(async (data: RecoveryProcessState) => {
    router.push('/login/')
  }, [])

  useEffect(() => {
    if (!processState.data) return
    reset(processState.data)
  }, [processState.data])

  return (
    <>
      <div className="mt-20">
        <h2 className="mt-4 text-xl font-medium text-gray-900">{t`login.new_password`}</h2>
      </div>
      <Alert type="warning" text={t`login.credentials_reminder`} className="mt-2" />
      <form
        onSubmit={handleSubmit(onUserDataSubmit)}
        className="grid grid-cols-1 mt-10 gap-y-6 gap-x-6 sm:grid-cols-2"
      >
        {processState.username && (
          <FormItem label="Email" error={errors.username?.message} className="col-span-full">
            <Tooltip content={t`copy`}>
              <Input
                readOnly
                onClick={() => {
                  navigator.clipboard.writeText(getValues('username'))
                  showToast('success', t`copied`)
                }}
                {...register('username', {
                  required: t`required_field`,
                })}
                hasError={Boolean(errors.username?.message)}
                type="email"
                autoComplete="email"
                placeRight={
                  <Tooltip content={t`copy`}>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(getValues('username'))
                        showToast('success', t`copied`)
                      }}
                      className="relative flex items-center justify-center px-3 py-2 -mx-1 text-gray-500 hover:text-gray-700"
                    >
                      <DuplicateIcon className="w-6 h-6" />
                    </button>
                  </Tooltip>
                }
              />
            </Tooltip>
          </FormItem>
        )}
        <FormItem label={t`password`} error={errors.password?.message} className="col-span-full">
          <Tooltip content={t`copy`}>
            <Input
              readOnly
              onClick={() => {
                navigator.clipboard.writeText(getValues('password'))
                showToast('success', t`copied`)
              }}
              {...register('password', {
                required: t`required_field`,
              })}
              hasError={Boolean(errors.password?.message)}
              autoComplete="new-password"
              placeRight={
                <Tooltip content={t`copy`}>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(getValues('password'))
                      showToast('success', t`copied`)
                    }}
                    className="relative flex items-center justify-center px-3 py-2 -mx-1 text-gray-500 hover:text-gray-700"
                  >
                    <DuplicateIcon className="w-6 h-6" />
                  </button>
                </Tooltip>
              }
            />
          </Tooltip>
        </FormItem>
        <div className="space-y-2 col-span-full">
          <Button type="submit" theme="primary" className="justify-center w-full space-x-1">
            <span>{t`login.go_to_login`}</span>
          </Button>
        </div>
      </form>
    </>
  )
}

export default ResetPasswordSuccess
